"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { useBillingOverview } from "@/lib/billing/useBillingOverview";
import { useStripePortal } from "@/lib/billing/useStripePortal";
import { orgRoleCanManageBilling } from "@/lib/org/org-constants";
import { BillingPaymentMethodBrandIcon } from "./billing.payment-method.brand-icon";

const formatCardBrand = (brand?: string | null) => {
  if (!brand) return "Card";
  return brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();
};

export const BillingPaymentTab = () => {
  const { orgRole } = useAuth();
  const canManageBilling = orgRoleCanManageBilling(orgRole);
  const { data } = useBillingOverview();
  const { isRedirecting, openPortal } = useStripePortal();

  const paymentMethods = data?.billing_overview.payment_methods ?? [];

  return (
    <section className="border border-border bg-card">
      <div className="flex flex-col gap-6 px-6 py-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-base font-medium text-foreground">Payment methods</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {canManageBilling
                ? "Update your card, billing address, and tax details in the billing portal."
                : "Cards and billing details on file for this organization."}
            </p>
          </div>
          {canManageBilling ? (
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => void openPortal(undefined, false)}
              disabled={isRedirecting}
            >
              Manage payment methods
            </Button>
          ) : null}
        </div>

        <div className="flex flex-col gap-5">
          {paymentMethods.length ? (
            paymentMethods.map((method) => {
              const isLink = method.type === "link";
              return (
                <div key={method.id} className="flex items-center gap-4">
                  <div
                    className="flex size-12 shrink-0 items-center justify-center border bg-sidebar text-muted-foreground"
                    aria-hidden
                  >
                    <BillingPaymentMethodBrandIcon brand={isLink ? "link" : method.brand} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-medium">
                        {isLink ? "Link" : `${formatCardBrand(method.brand)} ending in ${method.last4 ?? "—"}`}
                      </p>
                      {method.is_default ? (
                        <Badge variant="secondary" className="rounded-full text-xs">
                          Default
                        </Badge>
                      ) : null}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {isLink
                        ? (method.link_email ?? "Linked account")
                        : `Expires ${method.exp_month ?? "—"}/${method.exp_year ?? "—"}`}
                    </p>
                  </div>
                </div>
              );
            })
          ) : canManageBilling ? (
            <div className="flex items-center gap-4">
              <div
                className="flex size-12 shrink-0 items-center justify-center border bg-sidebar text-muted-foreground"
                aria-hidden
              >
                <BillingPaymentMethodBrandIcon />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium">No payment method on file</p>
                <p className="text-sm text-muted-foreground">
                  Add a card and your billing address in the billing portal.
                </p>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Payment methods are managed by billing admins.</p>
          )}
        </div>
      </div>
    </section>
  );
};
